/**
 * Rates that are artifacts rather than yields — the cross-provider guard.
 *
 * `morpho/rateSanity.ts` established the shape for one mechanism (a market
 * pinned at 100 % utilization publishing an ever-growing rate). This is the
 * same discipline for the two mechanisms that are not Morpho-specific:
 *
 *  1. **Annualization noise near maturity.** A fixed-term instrument's rate is
 *     a price deviation raised to `365 / daysToMaturity`. As that exponent
 *     grows the amplification is unbounded, so arbitrarily small price noise
 *     becomes an arbitrarily large APR — in either direction. A live Spectra
 *     PT with **5 hours left and $33 of TVL** published **−6.50 %** off a
 *     price sitting **0.4 bps** above par. Nothing about −6.50 % is a fact
 *     about that market.
 *  2. **Magnitude on dust.** A vault holding a few hundred dollars can print
 *     any number at all, because the share-price delta it is measured from is
 *     dominated by rounding and seeding. Yearn's ysOETH published
 *     **5,755,485 %** on **$341**.
 *
 * ## What this deliberately does NOT do
 *
 * **It never clamps a negative rate to zero.** A negative is a real, and
 * load-bearing, answer in three places we serve:
 *
 *  - a **realized** rate — a HyperCore book, a Strata junior tranche or a
 *    Yield Basis market can genuinely lose money over the window, and
 *    reporting 0 % would be the actual error;
 *  - a **fixed-term** instrument trading ABOVE par, which is an ordinary
 *    state when the underlying pays more than the PT's fixed leg — with real
 *    size and real time to maturity that is a fact, not noise, and it is
 *    exactly what a buyer needs to see;
 *  - a **Morpho-style vault socializing bad debt**, where the share price
 *    falls and the realized rate goes negative. Backchecked 2026-08-25
 *    against the live API: `netApy` and `apy` — the FORWARD, IRM-derived
 *    fields our fetcher reads — are non-negative across all 500 indexed
 *    vaults and structurally cannot be otherwise, while the REALIZED
 *    `dailyNetApy` / `weeklyNetApy` do go negative (4 vaults, incl. csUSDL at
 *    −100 % on $37 k). So a negative on a Morpho row today would be a bug in
 *    our path, but the day we read a realized field it becomes the truth, and
 *    a guard that had clamped it would hide precisely the event a depositor
 *    most needs to know about.
 *
 * The rule that separates the two is not the sign and not the magnitude: it
 * is whether the number is a **measurement of something** or an
 * **amplification of nothing**. Hence two conditions on every check, never
 * one — the Morpho lesson.
 */

/**
 * Price deviation from par, in basis points, below which a fixed-term rate
 * is noise rather than a quote.
 *
 * 10 bps is comfortably below any real fixed-income spread we serve and
 * orders of magnitude above the affected rows. Measured against the live
 * Spectra book: the artifact row sits at **0.4 bps** while the smallest
 * genuine market prices **108 bps** — a 250x separation, so the threshold's
 * exact value is not load-bearing.
 */
const NOISE_DEVIATION_BPS = 10

/**
 * APR magnitude (percent, absolute) beyond which a rate stops being a yield.
 *
 * Deliberately far above the Morpho guard's 100 %: this check applies to
 * every provider, including ones that genuinely run incentive campaigns in
 * the high hundreds, so it only has to catch numbers that no market pays.
 */
const IMPLAUSIBLE_APR_PERCENT = 1000

/**
 * TVL below which a rate is a measurement on dust.
 *
 * A vault this size cannot fund the rate it advertises for anyone, and its
 * share-price delta is dominated by seeding and rounding.
 */
const DUST_TVL_USD = 1_000

/**
 * Remaining tenor below which a fixed-term APR is a fact about the EXPONENT
 * rather than about the market.
 *
 * The dust pairing above was the only escape from the noise check, and it is
 * the wrong second condition for a big book in its final hours: `PT apxUSD
 * 27 Aug 2026` held **$12.1 M** and published 68 % at T−8 h, 236 % at T−1 h,
 * off share prices of 0.99978 and 0.99997. Both are sub-5-bps deviations —
 * the noise check had them — and both sailed past the size gate by four
 * orders of magnitude.
 *
 * A day is where the amplification stops being tolerable: one basis point of
 * quote noise moves the APR by ~3.7 points at 24 h remaining, ~15 points at
 * 6 h, and ~141 points at 1 h. Above the threshold the same book reads sanely
 * — that market printed 8.28 % a fortnight out off a 33 bps deviation, which
 * clears the noise check outright and is never touched by this arm.
 *
 * The two-condition discipline is intact: this only ever pairs WITH the
 * deviation test, never replaces it. A near-dated market whose price is
 * genuinely away from par is quoting a real, if short, rate and is kept.
 */
const AMPLIFYING_TENOR_SECS = 86_400

export interface FixedTermRateCheck {
  /** Nominal APR, percent. */
  aprPercent: number
  /** Unix seconds. */
  expiry: number
  /** Unix seconds; injected so tests never depend on the wall clock. */
  nowSecs: number
}

/**
 * The price deviation from par that a fixed-term APR implies over its
 * remaining life, in basis points (signed).
 *
 * This is the number the market actually set; the APR is that number put
 * through an exponent. Recovering it is what makes a near-maturity rate
 * comparable to a far-dated one at all.
 */
export const impliedDeviationBps = (c: FixedTermRateCheck): number => {
  const secs = c.expiry - c.nowSecs
  if (!(secs > 0)) return 0
  const years = secs / 31_536_000
  // (1 + apr)^years − 1, i.e. undoing the annualization.
  return (Math.pow(1 + c.aprPercent / 100, years) - 1) * 10_000
}

/**
 * True when a fixed-term rate is annualization noise: the underlying price
 * deviation is smaller than any real market prices.
 *
 * Sign-agnostic on purpose. A PT above par is a legitimate quote and a PT
 * below par is the ordinary one; what disqualifies BOTH is the deviation
 * being too small to be a price rather than a rounding artifact.
 *
 * Already-matured rows are not this function's business — a matured PT's
 * forward yield is zero and its provider should drop it outright (Pendle's
 * `includeExpired` default).
 */
export const isAnnualizationNoise = (c: FixedTermRateCheck): boolean => {
  if (!Number.isFinite(c.aprPercent)) return false
  if (!(c.expiry > c.nowSecs)) return false
  return Math.abs(impliedDeviationBps(c)) < NOISE_DEVIATION_BPS
}

/**
 * True when a rate's magnitude can only be an artifact, AND the row is too
 * small for it to matter to anyone.
 *
 * Both halves are required, exactly as in the Morpho guard: a four-digit APR
 * on a real vault is a story worth telling (and worth investigating), while
 * a tiny vault at a sane rate is an ordinary new listing.
 */
export const isImplausibleMagnitude = (
  aprPercent: number,
  totalAssetsUsd: number | undefined,
): boolean => {
  if (!Number.isFinite(aprPercent)) return false
  if (Math.abs(aprPercent) <= IMPLAUSIBLE_APR_PERCENT) return false
  return (totalAssetsUsd ?? 0) < DUST_TVL_USD
}

/**
 * The combined verdict for one row, given what the provider knows.
 *
 * `expiry` omitted ⇒ only the magnitude check applies (a perpetual vault has
 * no annualization exponent to blow up). Fixed-term rows additionally require
 * the noise check to be paired with a reason the number cannot be a
 * measurement of anything — either the book is dust, or the remaining tenor
 * is short enough that the exponent, not the market, is setting the figure.
 *
 * Size ALONE was the pairing until 2026-09-09, and it let a $12.1 M PT serve
 * 236 % in its last hour; see {@link AMPLIFYING_TENOR_SECS}. The arm is
 * additive, so a dust row still drops on the size condition at any tenor.
 *
 * ALREADY-MATURED rows are deliberately not this function's business: their
 * forward rate is zero by definition and `earnRateAtMaturity` states it where
 * the row is built, because a holder still needs the row to redeem.
 */
export const isUnearnableRate = (args: {
  aprPercent: number
  totalAssetsUsd?: number
  expiry?: number
  nowSecs?: number
}): boolean => {
  const { aprPercent, totalAssetsUsd, expiry } = args
  if (isImplausibleMagnitude(aprPercent, totalAssetsUsd)) return true
  if (expiry === undefined) return false
  const nowSecs = args.nowSecs ?? Math.floor(Date.now() / 1000)
  if (!isAnnualizationNoise({ aprPercent, expiry, nowSecs })) return false
  return (
    (totalAssetsUsd ?? 0) < DUST_TVL_USD ||
    expiry - nowSecs < AMPLIFYING_TENOR_SECS
  )
}
